/**
 * Storyboard page layout: 2 to 12 frames of one aspect ratio, each over a few caption
 * lines, drawn in millimetres on a printables sheet.
 *
 * The frames are as large as the sheet allows. For each way of arranging the frames in
 * columns, the frame width is limited by the page width across and by the page height
 * down (frame, caption lines and gap, row after row); the arrangement that allows the
 * widest frame wins. The block is centred across and starts under the header.
 */

import { type PageGeometry, r2 } from "@/lib/tool-kit/printables/page";

export const MIN_FRAMES = 2;
export const MAX_FRAMES = 12;
export const DEFAULT_FRAMES = 6;
export const MIN_LINES = 1;
export const MAX_LINES = 3;
export const DEFAULT_LINES = 2;

export type Aspect = "16:9" | "4:3";
export const ASPECT_VALUE: Record<Aspect, number> = {
  "16:9": 16 / 9,
  "4:3": 4 / 3,
};

/** Space between frames, and above the first row, in millimetres. */
export const GAP_MM = 5;
/** Height of the title strip, in millimetres. */
export const HEADER_MM = 16;
/** Height of one caption line, and the air between a frame and its first line. */
export const CAPTION_LINE_MM = 7;
export const CAPTION_PAD_MM = 1;

export interface Frame {
  x: number;
  y: number;
  /** y of each caption rule. */
  rules: number[];
}

export interface StoryboardGrid {
  cols: number;
  rows: number;
  frameWidth: number;
  frameHeight: number;
  frames: Frame[];
}

export function clampFrames(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_FRAMES;
  }
  return Math.min(MAX_FRAMES, Math.max(MIN_FRAMES, Math.round(value)));
}

export function clampLines(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_LINES;
  }
  return Math.min(MAX_LINES, Math.max(MIN_LINES, Math.round(value)));
}

function captionHeight(lines: number): number {
  return lines * CAPTION_LINE_MM + CAPTION_PAD_MM;
}

/** The widest frame a `cols`-column arrangement of `count` frames allows. */
function frameWidthFor(
  cols: number,
  count: number,
  aspect: number,
  lines: number,
  width: number,
  height: number
): number {
  const rows = Math.ceil(count / cols);
  const across = (width - (cols - 1) * GAP_MM) / cols;
  const down =
    ((height + GAP_MM) / rows - captionHeight(lines) - GAP_MM) * aspect;
  return Math.min(across, down);
}

export function bestGrid(
  count: number,
  aspect: Aspect,
  geometry: PageGeometry,
  lines: number
): StoryboardGrid {
  const frames = clampFrames(count);
  const captions = clampLines(lines);
  const ratio = ASPECT_VALUE[aspect];
  const height = geometry.contentHeight - HEADER_MM;
  let cols = 1;
  let best = 0;
  for (let c = 1; c <= frames; c += 1) {
    const w = frameWidthFor(
      c,
      frames,
      ratio,
      captions,
      geometry.contentWidth,
      height
    );
    if (w > best + 1e-9) {
      best = w;
      cols = c;
    }
  }
  const frameWidth = r2(best);
  const frameHeight = r2(best / ratio);
  const rows = Math.ceil(frames / cols);
  const left =
    geometry.left +
    (geometry.contentWidth - (cols * frameWidth + (cols - 1) * GAP_MM)) / 2;
  const top = geometry.top + HEADER_MM;
  const cell = frameHeight + captionHeight(captions) + GAP_MM;
  return {
    cols,
    rows,
    frameWidth,
    frameHeight,
    frames: Array.from({ length: frames }, (_, i) => {
      const x = r2(left + (i % cols) * (frameWidth + GAP_MM));
      const y = r2(top + Math.floor(i / cols) * cell);
      return {
        x,
        y,
        rules: Array.from({ length: captions }, (_, k) =>
          r2(y + frameHeight + CAPTION_PAD_MM + (k + 1) * CAPTION_LINE_MM)
        ),
      };
    }),
  };
}
